import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Package,
  PackageOpen,
  TriangleAlert,
  Boxes,
  ArrowDownToLine,
  ArrowUpFromLine,
  SlidersHorizontal,
  History,
  Euro,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_PRODUCT = {
  sku: "",
  name: "",
  category: "General",
  description: "",
  supplier: "",
  unit: "ud",
  purchase_price: 0,
  sale_price: 0,
  stock: 0,
  min_stock: 0,
  location: "",
  active: true,
};

const EMPTY_MOVEMENT = {
  movement_type: "entrada",
  quantity: "",
  reason: "",
  reference: "",
  unit_cost: "",
};

const CATEGORIES = [
  "General",
  "Climatización",
  "Fontanería",
  "Electricidad",
  "Calderas",
  "Piscinas",
  "Energía solar",
  "Herramientas",
  "Consumibles",
];

function eur(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: color + "15" }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({
    total_products: 0,
    low_stock: 0,
    out_of_stock: 0,
    stock_value: 0,
  });

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("todas");
  const [stockFilter, setStockFilter] = useState("todos");

  const [productOpen, setProductOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [editing, setEditing] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [movementForm, setMovementForm] = useState(EMPTY_MOVEMENT);
  const [movements, setMovements] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [movementSaving, setMovementSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);

    try {
      const [productsResponse, summaryResponse] = await Promise.all([
        api.get("/inventory/products"),
        api.get("/inventory/summary"),
      ]);

      setProducts(productsResponse.data || []);
      setSummary(summaryResponse.data || {});
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetProductForm = () => {
    setEditing(null);
    setProductForm(EMPTY_PRODUCT);
  };

  const openCreate = () => {
    resetProductForm();
    setProductOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setProductForm({
      sku: product.sku || "",
      name: product.name || "",
      category: product.category || "General",
      description: product.description || "",
      supplier: product.supplier || "",
      unit: product.unit || "ud",
      purchase_price: product.purchase_price ?? 0,
      sale_price: product.sale_price ?? 0,
      stock: product.stock ?? 0,
      min_stock: product.min_stock ?? 0,
      location: product.location || "",
      active: product.active !== false,
    });
    setProductOpen(true);
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!productForm.sku.trim() || !productForm.name.trim()) {
      toast.error("SKU y nombre son obligatorios.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...productForm,
        sku: productForm.sku.trim().toUpperCase(),
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        supplier: productForm.supplier.trim(),
        location: productForm.location.trim(),
        purchase_price: Number(productForm.purchase_price) || 0,
        sale_price: Number(productForm.sale_price) || 0,
        min_stock: Number(productForm.min_stock) || 0,
      };

      if (editing) {
        delete payload.stock;
        await api.put(
          `/inventory/products/${editing.id}`,
          payload
        );
        toast.success("Producto actualizado.");
      } else {
        payload.stock = Number(productForm.stock) || 0;
        await api.post("/inventory/products", payload);
        toast.success("Producto creado.");
      }

      setProductOpen(false);
      resetProductForm();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const openMovement = (product, type = "entrada") => {
    setSelectedProduct(product);
    setMovementForm({
      ...EMPTY_MOVEMENT,
      movement_type: type,
      unit_cost:
        type === "entrada"
          ? String(product.purchase_price || "")
          : "",
    });
    setMovementOpen(true);
  };

  const saveMovement = async (event) => {
    event.preventDefault();

    if (!selectedProduct || Number(movementForm.quantity) <= 0) {
      toast.error("Introduce una cantidad válida.");
      return;
    }

    setMovementSaving(true);

    try {
      const payload = {
        movement_type: movementForm.movement_type,
        quantity: Number(movementForm.quantity),
        reason: movementForm.reason.trim(),
        reference: movementForm.reference.trim(),
        unit_cost:
          movementForm.unit_cost === ""
            ? null
            : Number(movementForm.unit_cost),
      };

      await api.post(
        `/inventory/products/${selectedProduct.id}/movements`,
        payload
      );

      toast.success("Movimiento de stock registrado.");
      setMovementOpen(false);
      setSelectedProduct(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setMovementSaving(false);
    }
  };

  const openHistory = async (product) => {
    setSelectedProduct(product);
    setMovements([]);
    setHistoryOpen(true);
    setHistoryLoading(true);

    try {
      const { data } = await api.get(
        `/inventory/movements?product_id=${encodeURIComponent(
          product.id
        )}`
      );
      setMovements(data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setHistoryLoading(false);
    }
  };

  const remove = async (product) => {
    if (
      !window.confirm(
        `¿Archivar el producto "${product.name}"?`
      )
    ) {
      return;
    }

    setDeletingId(product.id);

    try {
      await api.delete(`/inventory/products/${product.id}`);
      toast.success("Producto archivado.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        products
          .map((product) => product.category)
          .filter(Boolean)
      )
    ).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        !normalized ||
        [
          product.sku,
          product.name,
          product.category,
          product.supplier,
          product.location,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesCategory =
        categoryFilter === "todas" ||
        product.category === categoryFilter;

      const matchesStock =
        stockFilter === "todos" ||
        (stockFilter === "bajo" && product.low_stock) ||
        (stockFilter === "sin_stock" &&
          Number(product.stock || 0) <= 0) ||
        (stockFilter === "disponible" &&
          Number(product.stock || 0) >
            Number(product.min_stock || 0));

      return matchesSearch && matchesCategory && matchesStock;
    });
  }, [products, query, categoryFilter, stockFilter]);

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Inventario"
        subtitle={`${products.length} productos y repuestos`}
        action={
          <Dialog
            open={productOpen}
            onOpenChange={(value) => {
              setProductOpen(value);
              if (!value) resetProductForm();
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo producto
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-3xl border-none bg-white p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing
                    ? "Editar producto"
                    : "Nuevo producto"}
                </DialogTitle>
                <p className="text-sm text-[#86868B]">
                  Registra el repuesto, su coste, precio de venta y stock mínimo.
                </p>
              </DialogHeader>

              <form onSubmit={saveProduct} className="mt-5 space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      SKU / Referencia
                    </label>
                    <input
                      value={productForm.sku}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          sku: event.target.value,
                        })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 uppercase outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">
                      Nombre
                    </label>
                    <input
                      value={productForm.name}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          name: event.target.value,
                        })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Categoría
                    </label>
                    <select
                      value={productForm.category}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          category: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      {CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Proveedor
                    </label>
                    <input
                      value={productForm.supplier}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          supplier: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Ubicación
                    </label>
                    <input
                      value={productForm.location}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          location: event.target.value,
                        })
                      }
                      placeholder="Estantería A-2"
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Descripción
                  </label>
                  <textarea
                    rows={3}
                    value={productForm.description}
                    onChange={(event) =>
                      setProductForm({
                        ...productForm,
                        description: event.target.value,
                      })
                    }
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Unidad
                    </label>
                    <select
                      value={productForm.unit}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          unit: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="ud">Unidad</option>
                      <option value="m">Metro</option>
                      <option value="kg">Kilogramo</option>
                      <option value="l">Litro</option>
                      <option value="caja">Caja</option>
                      <option value="rollo">Rollo</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Coste
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.purchase_price}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          purchase_price: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Venta
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.sale_price}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          sale_price: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      {editing ? "Stock actual" : "Stock inicial"}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.stock}
                      disabled={Boolean(editing)}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          stock: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none disabled:opacity-50"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Stock mínimo
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.min_stock}
                      onChange={(event) =>
                        setProductForm({
                          ...productForm,
                          min_stock: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setProductOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] py-3 font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Crear producto"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Dialog
        open={movementOpen}
        onOpenChange={(value) => {
          setMovementOpen(value);
          if (!value) setSelectedProduct(null);
        }}
      >
        <DialogContent className="max-w-lg rounded-3xl border-none bg-white p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Movimiento de stock
            </DialogTitle>
            <p className="text-sm text-[#86868B]">
              {selectedProduct?.sku} · {selectedProduct?.name}
            </p>
          </DialogHeader>

          <form onSubmit={saveMovement} className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Tipo
              </label>
              <select
                value={movementForm.movement_type}
                onChange={(event) =>
                  setMovementForm({
                    ...movementForm,
                    movement_type: event.target.value,
                  })
                }
                className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
              >
                <option value="entrada">Entrada</option>
                <option value="salida">Salida</option>
                <option value="ajuste">Ajustar stock total</option>
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  {movementForm.movement_type === "ajuste"
                    ? "Nuevo stock total"
                    : "Cantidad"}
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={movementForm.quantity}
                  onChange={(event) =>
                    setMovementForm({
                      ...movementForm,
                      quantity: event.target.value,
                    })
                  }
                  required
                  className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Coste unitario
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={movementForm.unit_cost}
                  onChange={(event) =>
                    setMovementForm({
                      ...movementForm,
                      unit_cost: event.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Motivo
              </label>
              <input
                value={movementForm.reason}
                onChange={(event) =>
                  setMovementForm({
                    ...movementForm,
                    reason: event.target.value,
                  })
                }
                placeholder="Compra, material utilizado, inventario..."
                className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Referencia
              </label>
              <input
                value={movementForm.reference}
                onChange={(event) =>
                  setMovementForm({
                    ...movementForm,
                    reference: event.target.value,
                  })
                }
                placeholder="Factura proveedor, OT-000001..."
                className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={movementSaving}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
            >
              {movementSaving && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {movementSaving
                ? "Guardando..."
                : "Registrar movimiento"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={historyOpen}
        onOpenChange={(value) => {
          setHistoryOpen(value);
          if (!value) setSelectedProduct(null);
        }}
      >
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto rounded-3xl border-none bg-white p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Historial de movimientos
            </DialogTitle>
            <p className="text-sm text-[#86868B]">
              {selectedProduct?.sku} · {selectedProduct?.name}
            </p>
          </DialogHeader>

          {historyLoading ? (
            <div className="flex min-h-48 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#0066CC]" />
            </div>
          ) : movements.length === 0 ? (
            <p className="py-10 text-center text-sm text-[#86868B]">
              No hay movimientos registrados.
            </p>
          ) : (
            <div className="mt-5 space-y-3">
              {movements.map((movement) => (
                <div
                  key={movement.id}
                  className="flex flex-col justify-between gap-3 rounded-2xl bg-[#F5F5F7] p-4 sm:flex-row sm:items-center"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        movement.movement_type === "entrada"
                          ? "bg-[#E9F9EE] text-[#248A3D]"
                          : movement.movement_type === "salida"
                            ? "bg-[#FFEBEA] text-[#D70015]"
                            : "bg-[#EAF2FC] text-[#0066CC]"
                      }`}
                    >
                      {movement.movement_type === "entrada" ? (
                        <ArrowDownToLine className="h-4 w-4" />
                      ) : movement.movement_type === "salida" ? (
                        <ArrowUpFromLine className="h-4 w-4" />
                      ) : (
                        <SlidersHorizontal className="h-4 w-4" />
                      )}
                    </div>

                    <div>
                      <p className="font-medium capitalize">
                        {movement.movement_type}
                      </p>
                      <p className="text-xs text-[#86868B]">
                        {movement.reason || "Sin motivo"}
                        {movement.reference
                          ? ` · ${movement.reference}`
                          : ""}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-mono font-semibold">
                      {Number(movement.quantity || 0).toLocaleString("es-ES")}
                    </p>
                    <p className="text-xs text-[#86868B]">
                      {movement.stock_before} → {movement.stock_after}
                    </p>
                    <p className="text-xs text-[#86868B]">
                      {String(movement.created_at || "").slice(0, 10)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Boxes}
          label="Productos activos"
          value={summary.total_products || 0}
          color="#0066CC"
        />
        <SummaryCard
          icon={TriangleAlert}
          label="Stock bajo"
          value={summary.low_stock || 0}
          color="#FF9500"
        />
        <SummaryCard
          icon={PackageOpen}
          label="Sin stock"
          value={summary.out_of_stock || 0}
          color="#FF3B30"
        />
        <SummaryCard
          icon={Euro}
          label="Valor del almacén"
          value={eur(summary.stock_value)}
          color="#5856D6"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por SKU, producto, proveedor o ubicación..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(event) =>
              setCategoryFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todas">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            value={stockFilter}
            onChange={(event) =>
              setStockFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todo el stock</option>
            <option value="disponible">Disponible</option>
            <option value="bajo">Stock bajo</option>
            <option value="sin_stock">Sin stock</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay productos"
          subtitle="Añade repuestos y materiales para controlar las existencias."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {filtered.map((product) => (
            <article
              key={product.id}
              className="mantix-card group p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                      product.low_stock
                        ? "bg-[#FFF4E5] text-[#C93400]"
                        : "bg-[#EAF2FC] text-[#0066CC]"
                    }`}
                  >
                    <Package className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold text-[#0066CC]">
                      {product.sku}
                    </p>
                    <h3 className="truncate font-medium text-[#1D1D1F]">
                      {product.name}
                    </h3>
                    <p className="text-xs text-[#86868B]">
                      {product.category || "General"}
                    </p>
                  </div>
                </div>

                {product.low_stock && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#FFF4E5] px-2.5 py-1 text-xs font-medium text-[#C93400]">
                    <TriangleAlert className="h-3 w-3" />
                    Stock bajo
                  </span>
                )}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">Stock</p>
                  <p className="mt-1 font-mono font-semibold">
                    {product.stock} {product.unit}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">Mínimo</p>
                  <p className="mt-1 font-mono font-semibold">
                    {product.min_stock} {product.unit}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">Coste</p>
                  <p className="mt-1 font-mono font-semibold">
                    {eur(product.purchase_price)}
                  </p>
                </div>
                <div className="rounded-xl bg-[#F5F5F7] p-3">
                  <p className="text-xs text-[#86868B]">Venta</p>
                  <p className="mt-1 font-mono font-semibold">
                    {eur(product.sale_price)}
                  </p>
                </div>
              </div>

              {(product.supplier || product.location) && (
                <div className="mt-4 space-y-1 text-sm text-[#86868B]">
                  {product.supplier && (
                    <p>Proveedor: {product.supplier}</p>
                  )}
                  {product.location && (
                    <p className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      {product.location}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#E8E8ED] pt-4">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      openMovement(product, "entrada")
                    }
                    className="flex items-center gap-1.5 rounded-full bg-[#E9F9EE] px-3 py-2 text-xs font-medium text-[#248A3D]"
                  >
                    <ArrowDownToLine className="h-3.5 w-3.5" />
                    Entrada
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      openMovement(product, "salida")
                    }
                    className="flex items-center gap-1.5 rounded-full bg-[#FFEBEA] px-3 py-2 text-xs font-medium text-[#D70015]"
                  >
                    <ArrowUpFromLine className="h-3.5 w-3.5" />
                    Salida
                  </button>
                </div>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => openHistory(product)}
                    title="Historial"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <History className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(product)}
                    title="Editar"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(product)}
                    disabled={deletingId === product.id}
                    title="Archivar"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-50"
                  >
                    {deletingId === product.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
